// toaster.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { NotificationService, Toast, ToastType } from '../../../core/services/notification.service';

@Component({
  selector: 'app-toaster',
  standalone: true,
  imports: [CommonModule, NgbToastModule],
  template: `
    <div class="toast-container position-fixed" [ngClass]="getPositionClass()">
      <ngb-toast
        *ngFor="let toast of toasts; trackBy: trackById"
        [class]="getToastClass(toast)"
        [autohide]="!toast.persistent"
        [delay]="toast.duration || 5000"
        (hidden)="onHidden(toast.id)"
        [animation]="true"
      >
        <!-- Header -->
        <ng-template ngbToastHeader *ngIf="toast.title">
          <div class="d-flex align-items-center flex-fill">
            <i [class]="getIconClass(toast)" class="me-2"></i>
            <strong class="me-auto">{{ toast.title }}</strong>
            <small class="text-muted" *ngIf="showTimestamp">{{
              getTimeAgo(toast.timestamp)
            }}</small>
          </div>
        </ng-template>

        <!-- Body -->
        <div class="toast-body-content">
          <div class="d-flex align-items-start">
            <i *ngIf="!toast.title" [class]="getIconClass(toast)" class="me-2 mt-1"></i>
            <div class="flex-fill">
              <div>{{ toast.message }}</div>

              <!-- Actions -->
              <div class="mt-2 d-flex gap-2" *ngIf="toast.actions && toast.actions.length > 0">
                <button
                  *ngFor="let action of toast.actions"
                  [class]="getActionClass(action.style)"
                  (click)="handleAction(action.action, toast.id)"
                  type="button"
                >
                  {{ action.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Progress Bar -->
          <div
            *ngIf="toast.progressBar && toast.duration && !toast.persistent"
            class="toast-progress-container"
          >
            <div
              class="toast-progress-bar"
              [class]="getProgressBarClass(toast.type)"
              [style.animation-duration.ms]="toast.duration"
            ></div>
          </div>
        </div>
      </ngb-toast>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      /* Progress bar animation */
      .toast-progress-container {
        position: relative;
        height: 3px;
        margin-top: 0.5rem;
        background-color: rgba(0, 0, 0, 0.1);
        overflow: hidden;
      }

      .toast-progress-bar {
        height: 100%;
        width: 100%;
        transform-origin: left;
        animation: countdown linear forwards;
      }

      @keyframes countdown {
        from {
          transform: scaleX(1);
        }
        to {
          transform: scaleX(0);
        }
      }

      /* Toast container styles */
      .toast-container {
        z-index: 9999999;
        pointer-events: none;
      }

      .toast-container > * {
        pointer-events: auto;
      }

      /* Position classes */
      .toast-container.top-start {
        top: 20px;
        left: 20px;
      }

      .toast-container.top-center {
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
      }

      .toast-container.top-end {
        top: 20px;
        right: 20px;
      }

      .toast-container.bottom-start {
        bottom: 20px;
        left: 20px;
      }

      .toast-container.bottom-center {
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
      }

      .toast-container.bottom-end {
        bottom: 20px;
        right: 20px;
      }

      /* Toast styling with ::ng-deep */
      ::ng-deep .toast {
        min-width: 300px;
        max-width: 500px;
        margin-bottom: 0.5rem;
        border: none;
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
      }

      ::ng-deep .toast.success-toast {
        background-color: #d1fae5;
        border-left: 4px solid #10b981;
      }

      ::ng-deep .toast.success-toast .toast-header {
        background-color: #a7f3d0;
        color: #065f46;
        border-bottom: 1px solid rgba(16, 185, 129, 0.2);
      }

      ::ng-deep .toast.error-toast {
        background-color: #fee2e2;
        border-left: 4px solid #ef4444;
      }

      ::ng-deep .toast.error-toast .toast-header {
        background-color: #fecaca;
        color: #991b1b;
        border-bottom: 1px solid rgba(239, 68, 68, 0.2);
      }

      ::ng-deep .toast.warning-toast {
        background-color: #fef3c7;
        border-left: 4px solid #f59e0b;
      }

      ::ng-deep .toast.warning-toast .toast-header {
        background-color: #fde68a;
        color: #92400e;
        border-bottom: 1px solid rgba(245, 158, 11, 0.2);
      }

      ::ng-deep .toast.info-toast {
        background-color: #dbeafe;
        border-left: 4px solid #3b82f6;
      }

      ::ng-deep .toast.info-toast .toast-header {
        background-color: #bfdbfe;
        color: #1e3a8a;
        border-bottom: 1px solid rgba(59, 130, 246, 0.2);
      }

      /* Toast body */
      .toast-body-content {
        padding: 0.75rem;
        position: relative;
      }

      /* Icon colors */
      .icon-success {
        color: #10b981;
      }
      .icon-error {
        color: #ef4444;
      }
      .icon-warning {
        color: #f59e0b;
      }
      .icon-info {
        color: #3b82f6;
      }

      /* Progress bar colors */
      .toast-progress-bar.progress-bar-success {
        background-color: #10b981;
      }
      .toast-progress-bar.progress-bar-error {
        background-color: #ef4444;
      }
      .toast-progress-bar.progress-bar-warning {
        background-color: #f59e0b;
      }
      .toast-progress-bar.progress-bar-info {
        background-color: #3b82f6;
      }

      /* Alternative progress bar style at bottom of toast */
      .toast-progress-bottom {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 4px;
        background-color: rgba(0, 0, 0, 0.1);
        overflow: hidden;
      }

      .toast-progress-bottom .toast-progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        transform-origin: left;
        animation: countdown linear forwards;
      }
    `,
  ],
})
export class ToasterComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  showTimestamp = true;
  private subscription?: Subscription;

  constructor(private readonly toasterService: NotificationService) {}

  ngOnInit() {
    this.subscription = this.toasterService.activeToasts$.subscribe((toasts) => {
      this.toasts = toasts;
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onHidden(id: string) {
    this.toasterService.dismiss(id);
  }

  handleAction(action: () => void, toastId: string) {
    action();
    // Optionally dismiss the toast after action
    // this.toasterService.dismiss(toastId);
  }

  trackById(index: number, toast: Toast): string {
    return toast.id;
  }

  getPositionClass(): string {
    const config = this.toasterService.getConfig();
    const position = config.position ?? 'top-right';

    // Map our position names to Bootstrap's position utility classes
    const positionMap: Record<string, string> = {
      'top-left': 'top-start',
      'top-center': 'top-center',
      'top-right': 'top-end',
      'bottom-left': 'bottom-start',
      'bottom-center': 'bottom-center',
      'bottom-right': 'bottom-end',
    };

    return positionMap[position] ?? 'top-end';
  }

  getToastClass(toast: Toast): string {
    const classes = [`${toast.type}-toast`];
    if (toast.customClass) {
      classes.push(toast.customClass);
    }
    return classes.join(' ');
  }

  getIconClass(toast: Toast): string {
    const iconMap: Record<ToastType, string> = {
      [ToastType.SUCCESS]: 'bi bi-check-circle-fill icon-success',
      [ToastType.ERROR]: 'bi bi-x-circle-fill icon-error',
      [ToastType.WARNING]: 'bi bi-exclamation-triangle-fill icon-warning',
      [ToastType.INFO]: 'bi bi-info-circle-fill icon-info',
    };

    // Return custom icon if provided, otherwise use default based on type
    if (toast.icon === 'spinner') {
      return 'spinner-border spinner-border-sm icon-info';
    }

    return iconMap[toast.type] ?? iconMap[ToastType.INFO];
  }

  getActionClass(style?: string): string {
    const styleMap: Record<string, string> = {
      primary: 'btn btn-sm btn-primary',
      secondary: 'btn btn-sm btn-secondary',
      danger: 'btn btn-sm btn-danger',
    };

    return styleMap[style ?? 'secondary'] ?? styleMap['secondary'];
  }

  getProgressBarClass(type: ToastType): string {
    return `progress-bar-${type}`;
  }

  getTimeAgo(timestamp: Date): string {
    const now = new Date();
    const diff = Math.floor((now.getTime() - new Date(timestamp).getTime()) / 1000);

    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }
}
